/**
 * Pure function to build Soniox-specific WebSocket query params for TranslationHost.
 *
 * Endpoint detection is always on server-side and is no longer a client choice. The former
 * "Finalization Strategy" control offered a "Manual / Push-to-Talk" mode that sent
 * sonioxEnableEndpointDetection=false and then relied on explicit {"type":"finalize"} triggers
 * that NO client ever sent — picking it produced a session that finalized nothing, which is the
 * blob failure investigated on 2026-08-22. Segment length is now bounded server-side by the
 * assembler's committed-prefix cut, which takes no client parameter.
 *
 * Server contract: param names match TranslationSessionContext.cs parsing EXACTLY.
 *   sonioxMaxEndpointDelayMs     → context.SonioxMaxEndpointDelayMs
 *   sonioxTranslationMode        → context.SonioxTranslationModeOverride (line 1057)
 *   sonioxEnableSpeakerDiarization → context.SonioxEnableSpeakerDiarization (line 1026)
 *   sonioxEnableLanguageIdentification → context.SonioxEnableLanguageIdentification (line 1032)
 *   sonioxLanguageHints          → context.SonioxLanguageHints (line 1051)
 *
 * @param {object} opts
 * @param {string} opts.sonioxMaxEndpointDelayMs  '' or numeric string (500–3000)
 * @param {string} opts.sonioxTranslationMode  '' | 'SonioxNative'
 * @param {boolean} opts.sonioxEnableSpeakerDiarization  true = server default (not emitted)
 * @param {boolean} opts.sonioxEnableLanguageIdentification  true = server default (not emitted)
 * @param {string} opts.sonioxLanguageHints  '' or comma-separated ISO codes
 * @returns {string[]} Array of 'param=value' strings to push into the WebSocket URL queryParams array.
 */
export function buildSonioxQueryParams({
    sonioxMaxEndpointDelayMs = '',
    sonioxTranslationMode = '',
    sonioxEnableSpeakerDiarization = true,
    sonioxEnableLanguageIdentification = true,
    sonioxLanguageHints = '',
}) {
    const params = [];

    // ── Translation mode ──────────────────────────────────────────────────────
    if (sonioxTranslationMode && sonioxTranslationMode !== '') {
        params.push(`sonioxTranslationMode=${encodeURIComponent(sonioxTranslationMode)}`);
    }

    // ── Speaker Diarization / Language ID — only emit when disabled (default is true) ──────
    if (!sonioxEnableSpeakerDiarization) {
        params.push('sonioxEnableSpeakerDiarization=false');
    }
    if (!sonioxEnableLanguageIdentification) {
        params.push('sonioxEnableLanguageIdentification=false');
    }

    // ── Max endpoint delay — bounds the wait AFTER speech stops, nothing during it ──
    if (sonioxMaxEndpointDelayMs !== '') {
        const delayVal = parseInt(sonioxMaxEndpointDelayMs, 10);
        if (Number.isFinite(delayVal) && delayVal >= 500 && delayVal <= 3000) {
            params.push(`sonioxMaxEndpointDelayMs=${delayVal}`);
        }
    }

    // ── Language hints ────────────────────────────────────────────────────────
    if (sonioxLanguageHints && sonioxLanguageHints.trim() !== '') {
        params.push(`sonioxLanguageHints=${encodeURIComponent(sonioxLanguageHints.trim())}`);
    }

    return params;
}
